<template>
  <RevDialog :name="MODAL_NAME" :title="modalTitle">
    <template #body>
      {{ infoText }}
    </template>
    <template #footer>
      <div class="flex flex-col items-center">
        <RevButton
          class="mb-8"
          full-width="always"
          :loading="isSubmitting"
          variant="primary"
          @click="handleSubmit"
        >
          {{ yesLabel }}
        </RevButton>
        <RevLink class="mt-12" @click="handleCloseModal">
          {{ noLabel }}
        </RevLink>
      </div>
    </template>
  </RevDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { sellerUsersAPI } from '@backmarket/http-api'
import type {
  SellerUser,
  SellerUserStateCode,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-users'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDialog } from '@ds/components/Dialog'
import { RevLink } from '@ds/components/Link'
import { closeModal } from '@ds/components/ModalBase'

import { MODAL_NAMES } from '../../../../Profile.constants'
import { USER_STATE_CODES } from '../../UserPermissions.constants'

import translations from './UserStateModal.translations'

const MODAL_NAME = MODAL_NAMES.USER_PERMISSIONS.ACTIVATE_OR_DEACTIVATE_USER

const props = defineProps<{
  userId: SellerUser['id']
  stateCode: SellerUserStateCode
}>()

const emit = defineEmits(['user-updated'])

const i18n = useI18n()
const { openErrorToast, openSuccessToast } = useTheToast()

const isSubmitting = ref(false)

const shouldActivateUser = computed(
  () => props.stateCode === USER_STATE_CODES.ACTIVATED,
)

const infoText = computed(() =>
  i18n(
    shouldActivateUser.value
      ? translations.areYouSureActivate
      : translations.areYouSureDelete,
  ),
)

const modalTitle = computed(() =>
  i18n(
    shouldActivateUser.value
      ? translations.modalTitleActivate
      : translations.modalTitleDelete,
  ),
)

const yesLabel = computed(() =>
  i18n(
    shouldActivateUser.value
      ? translations.yesActivate
      : translations.yesDelete,
  ),
)

const noLabel = computed(() =>
  i18n(
    shouldActivateUser.value ? translations.noActivate : translations.noKeep,
  ),
)

function handleCloseModal() {
  closeModal(MODAL_NAME)
}

async function handleSubmit() {
  isSubmitting.value = true
  try {
    await $httpFetch(sellerUsersAPI.patchSellerUser, {
      pathParams: { userId: props.userId },
      body: { state: props.stateCode },
    })
    openSuccessToast({ content: i18n(translations.userDeleted) })
    emit('user-updated')
  } catch {
    openErrorToast()
  } finally {
    isSubmitting.value = false
    closeModal(MODAL_NAME)
  }
}
</script>
